import React from 'react';
import { useModal } from 'store/modals';
import { LOCAL_STORAGE_KEYS, useLocalStorage } from 'utils/local-storage';
import { LocationPermissionDialog } from './LocationPermissionDialog';
import { useLocationAccess } from './use-location-access';
import { isGeolocationSupported } from './geolocation';

/**
 * Options for {@link useLocationPermissionPrompt}.
 */
type UseLocationPermissionPromptOptions = {
  /**
   * Gate that controls when the prompt logic may run. Typically set to `true`
   * only once the surrounding form/document has finished loading, so we never
   * act on a half-initialised state.
   */
  shouldRun: boolean;
  /**
   * Called with a "lat, lng" string whenever coordinates become available
   * (either because the user just enabled access, or because they had opted in
   * previously). Consumers use this to pre-fill their coordinates field.
   */
  onCoordinates: (coordinates: string) => void;
};

/**
 * Drives the location-permission experience for a form that supports GPS
 * coordinates.
 *
 * Runs once (per mount) when `shouldRun` becomes true:
 * - If geolocation is unsupported, does nothing.
 * - If the user already opted in, silently fetches fresh coordinates and hands
 *   them to `onCoordinates`.
 * - Otherwise, if the user has never been prompted, opens the informational
 *   dialog. Accepting it reports the coordinates back through `onCoordinates`.
 *
 * It is intentionally best-effort and never blocks rendering, keeping the app
 * fully usable for users who never enable location services.
 */
export function useLocationPermissionPrompt({
  shouldRun,
  onCoordinates,
}: UseLocationPermissionPromptOptions) {
  const { openModal } = useModal();
  const { isLocationAccessEnabled, refreshCoordinates } = useLocationAccess();
  const [hasPrompted, setHasPrompted] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.LOCATION_PERMISSION_PROMPTED,
  );

  const hasRunRef = React.useRef(false);
  // Keep the latest callback without forcing the effect to re-run.
  const onCoordinatesRef = React.useRef(onCoordinates);
  onCoordinatesRef.current = onCoordinates;

  React.useEffect(() => {
    // Only ever run this once, and only once the consumer says it is ready.
    if (!shouldRun || hasRunRef.current) {
      return;
    }
    hasRunRef.current = true;

    if (!isGeolocationSupported()) {
      return;
    }

    // The user already opted in previously - fetch fresh coordinates and let
    // the consumer decide whether to use them (e.g. only fill an empty field).
    if (isLocationAccessEnabled) {
      refreshCoordinates()
        .then((coordinates) => {
          if (coordinates) {
            onCoordinatesRef.current(coordinates);
          }
        })
        .catch(() => {
          // refreshCoordinates never rejects (errors are handled internally),
          // this handler only keeps the promise from floating.
        });
      return;
    }

    // Already asked before and the user did not opt in - respect that choice.
    if (hasPrompted) {
      return;
    }

    setHasPrompted(true);
    openModal({
      modalType: 'componentModal',
      modalProps: {
        children: React.createElement(LocationPermissionDialog, {
          onCoordinates: (coordinates: string) =>
            onCoordinatesRef.current(coordinates),
        }),
      },
    });
  }, [
    shouldRun,
    hasPrompted,
    isLocationAccessEnabled,
    openModal,
    refreshCoordinates,
    setHasPrompted,
  ]);
}

